import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import AuthModal from '@/components/AuthModal';
import { X } from 'lucide-react';

const NAV_ITEMS = [
  { id: 'numerologija', label: 'Numerologija', icon: 'stars', path: '/' },
  { id: 'skaiciu-zemelapis', label: 'Skaičių žemėlapis', icon: 'map', path: '/skaiciu-zemelapis' },
  { id: 'taro', label: 'Taro', icon: 'cards', path: null },
  { id: 'sielos-matrica', label: 'Sielos Matrica', icon: 'spiral', path: null },
  { id: 'runos', label: 'Runos', icon: 'rune', path: null },
  { id: 'vedu-astrologija', label: 'Vedų Astrologija', icon: 'sun', path: null },
  { id: 'vakaru-astrologija', label: 'Vakarų Astrologija', icon: 'moon', path: null },
  { id: 'kinu-zodiakai', label: 'Kinų Zodiakų Archetipai', icon: 'dragon', path: null },
  { id: 'numenaxis-ai', label: 'Klausk Numenaxis AI', icon: 'crystal', path: null },
];

// Custom minimal SVG icons matching the dark/gold theme
function NavIcon({ type, className = "w-5 h-5" }) {
  const props = { className, fill: "none", stroke: "currentColor", strokeWidth: 1.5, viewBox: "0 0 24 24" };
  switch (type) {
    case 'stars':
      return (<svg {...props}><path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" /></svg>);
    case 'cards':
      return (<svg {...props}><rect x="3" y="4" width="7" height="10" rx="1" /><rect x="14" y="4" width="7" height="10" rx="1" /><path strokeLinecap="round" d="M6.5 8v2M17.5 8v2M5 18h14" /></svg>);
    case 'spiral':
      return (<svg {...props}><path strokeLinecap="round" d="M12 3c4.97 0 9 4.03 9 9s-4.03 9-9 9-9-4.03-9-9 4.03-9 9-9z" opacity=".3" /><path strokeLinecap="round" d="M12 7c2.76 0 5 2.24 5 5s-2.24 5-5 5-5-2.24-5-5 2.24-5 5-5z" opacity=".6" /><circle cx="12" cy="12" r="1.5" fill="currentColor" /></svg>);
    case 'rune':
      return (<svg {...props}><path strokeLinecap="round" strokeLinejoin="round" d="M12 3v18M7 7l5 5-5 5M17 7l-5 5 5 5" /></svg>);
    case 'sun':
      return (<svg {...props}><circle cx="12" cy="12" r="4" /><path strokeLinecap="round" d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" /></svg>);
    case 'moon':
      return (<svg {...props}><path strokeLinecap="round" strokeLinejoin="round" d="M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z" /></svg>);
    case 'dragon':
      return (<svg {...props}><path strokeLinecap="round" strokeLinejoin="round" d="M15 3l-3 3-3-3M9 6v3c0 2 1 3 3 3s3-1 3-3V6M7 15c0 3 2.5 6 5 6s5-3 5-6M9 15h6" /><circle cx="10" cy="9" r=".5" fill="currentColor" /><circle cx="14" cy="9" r=".5" fill="currentColor" /></svg>);
    case 'crystal':
      return (<svg {...props}><path strokeLinecap="round" strokeLinejoin="round" d="M12 2l4 7h-8l4-7zM8 9l-3 6h14l-3-6M5 15l3 7h8l3-7" /></svg>);
    case 'key':
      return (<svg {...props}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 013 3c0 .825-.333 1.572-.873 2.113l-.007.007-.007.006A2.993 2.993 0 0115.75 11.25a3 3 0 01-3-3 3 3 0 013-3zm0 0v0zM15.75 11.25l-7.5 7.5-2.25-2.25" /></svg>);
    case 'map':
      return (<svg {...props}><rect x="3.5" y="3.5" width="7" height="7" rx="1.5" /><rect x="13.5" y="3.5" width="7" height="7" rx="1.5" /><rect x="3.5" y="13.5" width="7" height="7" rx="1.5" /><rect x="13.5" y="13.5" width="7" height="7" rx="1.5" /></svg>);
    default:
      return null;
  }
}

export default function GlobalNavMenu({ isOpen, onClose }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [showAuth, setShowAuth] = useState(false);

  if (!isOpen) return null;

  const handleNavClick = (item) => {
    if (item.path) {
      navigate(item.path);
      onClose();
    }
    // Items with path: null are "Coming Soon" — no navigation
  };

  const handleLogin = () => {
    setShowAuth(true);
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
        onClick={onClose}
      />

      {/* Drawer */}
      <nav
        className="fixed top-0 left-0 h-full w-72 sm:w-80 bg-gradient-to-b from-[#0f0f23] via-[#1a1a3e] to-[#0f0f23] border-r border-[#d4af37]/20 z-50 flex flex-col shadow-2xl"
        data-testid="global-nav-menu"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-[#d4af37]/15">
          <div className="flex items-center gap-3">
            <img
              src="/images/numenaxis-symbol-transparent.png"
              alt="NUMENAXIS"
              className="w-8 h-8 object-contain"
            />
            <span className="font-display text-lg text-[#d4af37] tracking-wide">NUMENAXIS</span>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full text-foreground/50 hover:text-[#d4af37] hover:bg-[#d4af37]/10 transition-colors"
            data-testid="nav-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Nav Items */}
        <div className="flex-1 overflow-y-auto py-3">
          {NAV_ITEMS.map((item) => {
            const isActive = item.path && location.pathname === item.path;
            const isComingSoon = !item.path;
            return (
              <button
                key={item.id}
                onClick={() => handleNavClick(item)}
                className={`w-full flex items-center gap-4 px-5 py-3.5 text-left transition-all duration-200 group ${
                  isActive
                    ? 'bg-[#d4af37]/15 text-[#d4af37] border-r-2 border-[#d4af37]'
                    : isComingSoon
                      ? 'text-foreground/30 cursor-default'
                      : 'text-foreground/70 hover:bg-[#d4af37]/8 hover:text-[#d4af37]'
                }`}
                data-testid={`nav-${item.id}`}
              >
                <NavIcon
                  type={item.icon}
                  className={`w-5 h-5 flex-shrink-0 ${
                    isActive ? 'text-[#d4af37]' : isComingSoon ? 'text-foreground/20' : 'text-foreground/40 group-hover:text-[#d4af37]/70'
                  }`}
                />
                <span className="text-sm font-medium">{item.label}</span>
                {isComingSoon && (
                  <span className="ml-auto text-[10px] uppercase tracking-wider text-foreground/20 font-semibold">Soon</span>
                )}
              </button>
            );
          })}
        </div>

        {/* Footer — Login Button */}
        <div className="border-t border-[#d4af37]/15 p-4">
          {user ? (
            <div className="flex items-center gap-3 px-2">
              <div className="w-8 h-8 rounded-full bg-[#d4af37]/20 flex items-center justify-center">
                <span className="text-xs font-bold text-[#d4af37]">{user.name?.[0] || user.email?.[0] || '?'}</span>
              </div>
              <div className="min-w-0">
                <p className="text-sm text-foreground/80 truncate">{user.name || user.email}</p>
                <p className="text-xs text-foreground/40 truncate">{user.isVIP ? 'VIP' : 'Narys'}</p>
              </div>
            </div>
          ) : (
            <button
              onClick={handleLogin}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg border border-[#d4af37]/30 text-[#d4af37] hover:bg-[#d4af37]/10 transition-colors"
              data-testid="nav-login-btn"
            >
              <NavIcon type="key" className="w-5 h-5" />
              <span className="text-sm font-medium">Prisijungti</span>
            </button>
          )}
        </div>
      </nav>

      {/* Auth Modal */}
      {showAuth && (
        <AuthModal
          isOpen={showAuth}
          onClose={() => setShowAuth(false)}
          defaultTab="login"
        />
      )}
    </>
  );
}
